import React from 'react';
import LegalPage from '../components/LegalPage';

export default function Privacy() {
  return (
    <LegalPage kicker="Legal" title="Privacy Policy" date="Last updated: June 2, 2026">
      <h2>1. Introduction</h2>
      <p>
        Welcome to <strong>Zero Immersive Inc</strong> ("we," "our," or "us").
        Your privacy matters to us. This Privacy Policy explains how we collect,
        use, store, and share your data when you use our mobile games ("Games"
        or "Services").
      </p>
      <p>By using our Services, you agree to the terms of this Privacy Policy.</p>
      <p>This policy is designed to comply with:</p>
      <ul>
        <li><strong>GDPR</strong> — General Data Protection Regulation (EEA / UK users)</li>
        <li><strong>CCPA/CPRA</strong> — California Consumer Privacy Act, as amended (California residents)</li>
      </ul>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Personal data (when you provide it)</h3>
      <ul>
        <li>Name, email, or other contact details if you reach out for support.</li>
        <li>Payment details processed securely via platform providers (Google Play, App Store). We do not store payment information.</li>
        <li>User-generated content (support tickets, chat, profile info if applicable).</li>
      </ul>
      <p><strong>Opt-out:</strong> You may choose not to provide this information, but certain features may not work.</p>

      <h3>2.2 Non-personal data (collected automatically)</h3>
      <ul>
        <li>Device data: model, operating system, language, advertising ID, IP address (stored temporarily for security).</li>
        <li>Usage and gameplay data: session duration, in-game actions, ad interactions.</li>
        <li>Crash and performance logs for debugging and improvement.</li>
      </ul>

      <h3>2.3 Data from partners</h3>
      <ul>
        <li>Ad networks provide data on ad interactions and campaign performance.</li>
        <li>Analytics partners provide aggregated data to help us improve our games.</li>
      </ul>

      <h3>2.4 Device permissions</h3>
      <p>Some features may require permissions (e.g., storage, notifications). You can disable permissions in device settings, but certain features may not work properly.</p>

      <h2>3. How We Use Your Information</h2>
      <ul>
        <li>Provide and improve the gaming experience.</li>
        <li>Deliver advertising, including personalized advertisements where permitted.</li>
        <li>Prevent fraud and ensure security.</li>
        <li>Analyze gameplay trends to improve features.</li>
      </ul>
      <p><strong>Consent:</strong> Where required (such as in the EEA and UK), we ask for explicit consent before using data for personalized ads. Users identified as below the age of digital consent in their jurisdiction are not served personalized advertising (see Section 7).</p>

      <h2>4. Third-Party Services We Use</h2>
      <h3>4.1 Advertising and monetization SDKs</h3>
      <p>Our games may show ads and integrate with third-party monetization services. Each provider may collect, process, and share personal data (including advertising identifiers and device information) for advertising purposes, including serving interest-based advertising and improving their advertising services:</p>
      <ul>
        <li>AdMob (Google) — <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
        <li>AppLovin — <a href="https://legal.applovin.com/privacy" target="_blank" rel="noopener noreferrer">https://legal.applovin.com/privacy</a></li>
        <li>Liftoff — <a href="https://liftoff.io/privacy-policy/" target="_blank" rel="noopener noreferrer">https://liftoff.io/privacy-policy/</a></li>
        <li>Mintegral — <a href="https://www.mintegral.com/en/privacy/" target="_blank" rel="noopener noreferrer">https://www.mintegral.com/en/privacy/</a></li>
        <li>Unity Ads — <a href="https://unity.com/legal/game-player-and-app-user-privacy-policy" target="_blank" rel="noopener noreferrer">https://unity.com/legal/game-player-and-app-user-privacy-policy</a></li>
      </ul>
      <p>We work with AppLovin to deliver ads in our mobile applications and on other devices and/or platforms. For more information about AppLovin's collection and use of your information, visit: <a href="https://legal.applovin.com/privacy" target="_blank" rel="noopener noreferrer">https://legal.applovin.com/privacy</a>.</p>
      <p>Certain third-party measurement partners may also collect and process personal data as part of the Open Measurement Working Group to perform ad measurement and related services.</p>

      <h3>4.2 Analytics and performance monitoring</h3>
      <ul>
        <li>AppMetrica</li>
        <li>SolarEngine (internal analytics)</li>
        <li>Firebase</li>
      </ul>
      <p>Each third-party provider has its own privacy practices. We recommend reviewing their policies.</p>

      <h2>5. Data Sharing and Disclosure</h2>
      <p>We do not sell your personal information for monetary consideration. However, our use of advertising partners to deliver personalized or interest-based ads may be considered a "sale" or "share" of personal information for cross-context behavioral advertising under certain laws, including the CCPA/CPRA. You may opt out of this sharing at any time (see Section 9), and we honor opt-out signals (including "Do Not Sell or Share" and Global Privacy Control signals) where applicable.</p>
      <p>Aside from the above, we may share data only:</p>
      <ul>
        <li>To comply with legal obligations.</li>
        <li>To protect rights, safety, and prevent fraud.</li>
        <li>In case of a business transfer, merger, or acquisition.</li>
        <li>As aggregated, anonymized reports for industry research.</li>
      </ul>

      <h2>6. Data Retention and Deletion</h2>
      <p>We retain your data only as long as needed for the purposes described above.</p>
      <p>You may request deletion of your personal data at any time by contacting us at <a href="mailto:accounts@zeroimmersive.com">accounts@zeroimmersive.com</a>. We will process verified deletion requests in accordance with applicable law.</p>

      <h2>7. Children's Privacy</h2>
      <p>Our Services are intended for general audiences and are not directed to children under the age of digital consent in their jurisdiction (for example, under 13 under COPPA in the United States, or the applicable age of 13–16 under GDPR in the EEA/UK). We do not knowingly collect personal data from, or serve personalized advertising to, children below the applicable age.</p>
      <p>We take steps to identify and prevent under-age users from receiving personalized advertising or initializing advertising SDKs that process personal data for advertising. Where age-signal mechanisms or app-store age APIs are available, we use them to apply these protections. If you are a parent or guardian and believe your child has provided personal data, please contact us and we will delete it.</p>

      <h2>8. Security</h2>
      <p>We apply commercially reasonable measures to safeguard your data. However, no system is 100% secure, and we cannot guarantee absolute security.</p>

      <h2>9. Your Rights</h2>
      <p>Depending on your location, you may have rights to:</p>
      <ul>
        <li>Access and request a copy of your data.</li>
        <li>Request correction or deletion.</li>
        <li>Opt out of targeted/interest-based advertising and the "sale" or "sharing" of your personal information.</li>
        <li>Withdraw consent where applicable.</li>
      </ul>
      <p>To exercise any of these rights, contact us at <a href="mailto:accounts@zeroimmersive.com">accounts@zeroimmersive.com</a>.</p>

      <h2>10. Changes to This Policy</h2>
      <p>We may update this Privacy Policy occasionally. Updates will be posted here with a revised "Last Updated" date. If significant changes occur, we may notify you through in-game messages or email.</p>

      <h2>11. Contact Us</h2>
      <p>If you have any questions about this Privacy Policy, please contact us:</p>
      <ul>
        <li>Email: <a href="mailto:accounts@zeroimmersive.com">accounts@zeroimmersive.com</a></li>
        <li>Phone: <a href="tel:+16475104511">+1 (647) 510-4511</a></li>
        <li>Location: Mississauga, Ontario, Canada</li>
      </ul>
      <p>Company name: Zero Immersive Inc</p>
    </LegalPage>
  );
}
